import React, { useState, Suspense, lazy, useEffect, useLayoutEffect, useMemo, createContext, useContext } from "react";
import Sidebar from "./components/common/sidebar/Sidebar";
import Header from "./components/common/header/Header";
import LoadingScreen from "./components/common/LoadingScreen";
import { ThemeProvider } from "@mui/material/styles";
import minimalTheme from "./theme";
import SupportFAB from "./components/common/feedback/SupportFAB";
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Http from "./services/Http";
import Page404 from "./pages/Page404/Page404";
import dataService from "./services/data.service";
import { HMGProvider } from "./HMGContext";
import { useHMG } from "./HMGContext";
import { UserPreferencesProvider } from "./contexts/UserPreferencesContext";
import { SocketProvider } from "./contexts/SocketProvider";
import ToastProvider from "./components/common/Toast/ToastProvider";
import { provideGlobalGridOptions } from 'ag-grid-community';
import { LicenseManager } from 'ag-grid-enterprise';
import ViewAsUser from "./components/common/ViewAsUser";
import HMGReprocessingLoader from "./components/common/Loaders/HMGReprocessingLoader";
import PlanReprocessingLoader from "./components/common/Loaders/PlanReprocessingLoader";
import { JobProgressProvider } from "./contexts/JobProgressContext";
import GlobalJobProgress from "./components/common/Loaders/GlobalJobProgress";
import { TOAST_LAYOUT } from "./constants/keywords";
import { lazyWithRetry } from "./Utils/lazyWithRetry";
import { initializeAgGridEnterpriseModules } from "./Utils/agGridModules";
import { TpxPermissionsProvider, useTpxPermissions } from "./contexts/TpxPermissionsContext";
import NoAccessPlans from "./components/common/NoAccess/NoAccessPlans";
import RouteChunkErrorBoundary from "./components/common/RouteChunkErrorBoundary";

// AG Grid License and Module Registration
// Note: Modules must be registered synchronously before components use AG Grid
const agGridLicenseKey = process.env.REACT_APP_AG_GRID_LICENSE_KEY;
if (!agGridLicenseKey || !agGridLicenseKey.trim()) {
  throw new Error(
    "Missing AG Grid Enterprise license key. Set REACT_APP_AG_GRID_LICENSE_KEY in the active .env file."
  );
}
LicenseManager.setLicenseKey(agGridLicenseKey);

// Initialize AG Grid global options
provideGlobalGridOptions({
  theme: "legacy",
  localeText: {
    pageSizeSelectorLabel: "Rows per page:",
    to: "-",
    of: "of",
    page: "",
  },
});

// Register AG Grid modules synchronously - required before any components use AG Grid
// This ensures modules are available when components mount
initializeAgGridEnterpriseModules();

// Using lazyWithRetry utility for all route-level lazy imports
// This provides retry logic for ChunkLoadErrors

const Home = lazyWithRetry(() => import("./pages/all-plans"));
const EditPlanPage = lazyWithRetry(() =>
  import("./pages/edit-plans/edit-plans.service")
);
const ChartOfAccount = lazyWithRetry(() => import("./pages/coa-management"));
const SettingsPage = lazyWithRetry(() => import("./pages/settings-page"));
const CritiquePage = lazyWithRetry(() => import("./pages/critique"));
const AllCritiques = lazyWithRetry(() => import("./pages/critique/AllCritiques"));
const EditCritiqueTemplate = lazyWithRetry(() => import("./pages/critique/EditCritiqueTemplate"));
const EditCritique = lazyWithRetry(() => import("./pages/critique/EditCritique"));
const ExportReports = lazyWithRetry(() => import("./pages/exports"));
const DataMigrationPage = lazyWithRetry(() => import("./pages/data-migration"));
const PairedDailyDedupPage = lazyWithRetry(() => import("./pages/paired-daily-dedup"));
const AppStateContext = createContext(null);

export const useAppState = () => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error("useAppState must be used within AppStateProvider");
  }
  return context;
};

const loadConfig = (env) => {
  switch (env) {
    case "development":
      return import("./auth_config.dev.json");
    case "uat":
      return import("./auth_config.uat.json");
    case "prod":
      return import("./auth_config.prod.json");
    // default:
      // return import("./auth_config.json"); // Default to development config
  }
};

/**
 * UAT/prod reverse proxies sometimes serve or redirect to a trailing slash (e.g. /planVersion/236/).
 * CRA dev server usually does not, so the bug appears only in deployed envs. React Router does not
 * read `history.replaceState` from App — use navigate(..., { replace: true }) inside the router tree.
 */
const NormalizeTrailingSlash = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    const { pathname, search, hash } = location;
    if (pathname.length <= 1 || !pathname.endsWith("/")) return;
    const path = pathname.replace(/\/+$/, "") || "/";
    navigate(`${path}${search}${hash}`, { replace: true });
  }, [location, navigate]);

  return null;
};

const Layout = () => {
  const { 
    isSidebarOpen, 
    toggleSidebar, 
    isEdited, 
    setIsEdited, 
    refreshPageData, 
    setRefreshPageData,
    showVarianceInHighlights,
    setShowVarianceInHighlights,
    showVarianceInRollups,
    setShowVarianceInRollups,
  } = useAppState();

  return (
    <TpxPermissionsProvider>
    <HMGProvider>
      <UserPreferencesProvider>
        <NormalizeTrailingSlash />
        <ViewAsUser />
        <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
        <div className={`flex-1 flex flex-col transition-all duration-300 relative min-h-0 min-w-0`}>
          <ToastProvider />
          <Header
            isEdited={isEdited}
            setIsEdited={setIsEdited}
            refreshPageData={refreshPageData}
            setRefreshPageData={setRefreshPageData}
            setShowVarianceInHighlights={setShowVarianceInHighlights}
            setShowVarianceInRollups={setShowVarianceInRollups}
          />
          <div className={`${TOAST_LAYOUT.TOP_NOTICE_CLASS} fixed right-6 top-[76px] z-50 flex flex-col gap-3 items-end`}>
            <HMGReprocessingLoader />
            <PlanReprocessingLoader />
            <GlobalJobProgress />
          </div>

          <div className="tpx-page-container flex-1 min-h-0 overflow-y-auto">
            <Suspense fallback={<LoadingScreen position={"fixed"} />}>
              <Outlet />
            </Suspense>
          </div>
        </div>
        <SupportFAB />
      </UserPreferencesProvider>
    </HMGProvider>
    </TpxPermissionsProvider>
  );
};

const EditPlanPageWrapper = () => {
  const {
    setIsEdited,
    refreshPageData,
    setRefreshPageData,
    showVarianceInRollups,
    showVarianceInHighlights,
    setShowVarianceInHighlights,
    setShowVarianceInRollups,
  } = useAppState();
  const { planView } = useTpxPermissions();
  const { selectedHMG } = useHMG();

  if (!planView) {
    // Redirect so Header doesn't fetch/show plan details for unauthorized users.
    return <Navigate to="/" replace />;
  }

  return (
    <EditPlanPage
      key={selectedHMG?.id ?? dataService.getSelectedHMGId()}
      setIsEditedParent={setIsEdited}
      refreshPageData={refreshPageData}
      setRefreshPageData={setRefreshPageData}
      showVarianceInRollups={showVarianceInRollups}
      showVarianceInHighlights={showVarianceInHighlights}
      setShowVarianceInHighlights={setShowVarianceInHighlights}
      setShowVarianceInRollups={setShowVarianceInRollups}
    />
  );
};

const CoaPageWrapper = () => {
  const { coaView } = useTpxPermissions();
  if (!coaView) {
    return <Navigate to="/" replace />;
  }
  return <ChartOfAccount />;
};

const SettingsPageWrapper = () => {
  const { settingsView } = useTpxPermissions();
  if (!settingsView) {
    return <Navigate to="/" replace />;
  }
  return <SettingsPage />;
};

const ExportReportsWrapper = () => {
  const { planningReportView } = useTpxPermissions();
  if (!planningReportView) {
    return <Navigate to="/" replace />;
  }
  return <ExportReports />;
};

const CritiqueIndexWrapper = () => {
  const { critiqueView, critiqueTemplateView } = useTpxPermissions();
  if (critiqueTemplateView) {
    return <Navigate to="/critique/templates" replace />;
  }
  if (critiqueView) {
    return <Navigate to="/critique/all" replace />;
  }
  return <Navigate to="/" replace />;
};

const CritiqueTemplatesWrapper = () => {
  const { critiqueTemplateView } = useTpxPermissions();
  if (!critiqueTemplateView) {
    return <Navigate to="/" replace />;
  }
  return <CritiquePage />;
};

const AllCritiquesWrapper = () => {
  const { critiqueView } = useTpxPermissions();
  if (!critiqueView) {
    return <Navigate to="/" replace />;
  }
  return <AllCritiques />;
};

function App() {
  const {
    isLoading,
    error,
    user,
    isAuthenticated,
    loginWithRedirect,
    logout,
    handleRedirectCallback,
  } = useAuth0();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const [config, setConfig] = useState(null);
  const [refreshPageData, setRefreshPageData] = useState();
  const [isUserDataLoading, setIsUserDataLoading] = useState(true);
  const [showVarianceInHighlights, setShowVarianceInHighlights] = useState();
  const [showVarianceInRollups, setShowVarianceInRollups] = useState();
  const [isEdited, setIsEdited] = useState(false);

  useEffect(() => {
    const env = process.env.REACT_APP_ENV; // The environment variable
    loadConfig(env).then((configFile) => {
      setConfig(configFile.default); // .default for dynamically imported JSON
    });

    Http.setTokenFunctions(getAccessTokenSilently, getIdTokenClaims, logout);
    Http.init(); // Initialize Http with the functions
  }, [getAccessTokenSilently, getIdTokenClaims]);

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const logoutWithRedirect = () =>
    logout({
      returnTo: window.location.origin,
    });

  const [selectedHMG, setSelectedHMG] = useState(); // For handling active hmg

  useEffect(() => {
  if (!isLoading && !isAuthenticated) {
      loginWithRedirect();
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  useEffect(() => {
    if (!isLoading && isAuthenticated && user?.email && user.email_verified) {
      dataService.fetchUserMeta(user.email).then((res) => {
        if (res) {
          if (res.hmg.length <= 0) {
            logoutWithRedirect();
            alert("No hotels/properties mapped to your user. Please contact otelier team.");
          } else {
            setIsUserDataLoading(false);
            setSelectedHMG(
              dataService.getHMGList().find((f) => f.id === dataService.getSelectedHMGId())
            );
          }
        }
      });
    }
  }, [isLoading, isAuthenticated, user]);

  const appStateValue = useMemo(() => ({
    isSidebarOpen,
    toggleSidebar,
    isEdited,
    setIsEdited,
    refreshPageData,
    setRefreshPageData,
    showVarianceInHighlights,
    setShowVarianceInHighlights,
    showVarianceInRollups,
    setShowVarianceInRollups,
  }), [
    isSidebarOpen,
    isEdited,
    refreshPageData,
    showVarianceInHighlights,
    showVarianceInRollups,
  ]);

  const router = useMemo(() => createBrowserRouter([
    {
      path: "/",
      element: <Layout />,
      errorElement: <RouteChunkErrorBoundary />,
      children: [
        { index: true, element: <Home /> },
        { path: "ChartOfAccounts", element: <CoaPageWrapper /> },
        { path: "planVersion/:id", element: <EditPlanPageWrapper /> },
        { path: "settings", element: <SettingsPageWrapper /> },
        { path: "exports", element: <ExportReportsWrapper /> },
        { path: "export", element: <ExportReportsWrapper /> },
        { path: "data-migration", element: <DataMigrationPage /> },
        { path: "paired-daily-dedup", element: <PairedDailyDedupPage /> },
        { path: "critique", element: <CritiqueIndexWrapper /> },
        { path: "critique/templates", element: <CritiqueTemplatesWrapper /> },
        { path: "critique/all", element: <AllCritiquesWrapper /> },
        { path: "critique/template/:id", element: <EditCritiqueTemplate /> },
        { path: "critique/details/:id", element: <EditCritique /> },
        { path: "*", element: <Page404 /> },
      ],
    },
  ], {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    },
  }), []);

  if (isLoading || isUserDataLoading) {
    return <LoadingScreen position={"fixed"} />;
  }

  return (
    <div className={`h-screen flex overflow-hidden bg-[#f3f5f7] global_border`}>
      <ThemeProvider theme={minimalTheme}>
        <JobProgressProvider>
          <SocketProvider>
            <AppStateContext.Provider value={appStateValue}>
              <RouterProvider router={router} />
            </AppStateContext.Provider>
          </SocketProvider>
        </JobProgressProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
